import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPut } from '../config/apiClient';
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  getCurrentSubscription,
} from '../utils/pushNotifications';

export default function NotificationSettings() {
  const { authToken } = useAuth();
  const [pushSupported] = useState(isPushSupported());
  const [pushPermission, setPushPermission] = useState(getNotificationPermission());
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [message, setMessage] = useState(null);

  const checkSubscription = useCallback(async () => {
    const sub = await getCurrentSubscription();
    setIsSubscribed(!!sub);
  }, []);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  useEffect(() => {
    async function loadPreferences() {
      try {
        const data = await apiGet('/notifications/preferences');
        setPreferences(data);
      } catch {
        setPreferences({
          price_drop_alerts: true,
          price_increase_alerts: true,
          receipt_reminders: true,
          promotions: true,
          email_fallback: true,
        });
      }
      setPrefsLoading(false);
    }
    loadPreferences();
  }, [authToken]);

  const handleEnablePush = async () => {
    setSubscribing(true);
    setMessage(null);
    const result = await subscribeToPush(authToken);
    if (result.success) {
      setIsSubscribed(true);
      setPushPermission('granted');
      setMessage({ type: 'success', text: 'Push notifications enabled!' });
    } else if (result.reason === 'permission_denied') {
      setPushPermission('denied');
      setMessage({ type: 'error', text: 'Notification permission was denied. Please enable it in your browser settings.' });
    } else {
      setMessage({ type: 'error', text: 'Failed to enable push notifications. Please try again.' });
    }
    setSubscribing(false);
  };

  const handleDisablePush = async () => {
    setSubscribing(true);
    setMessage(null);
    const result = await unsubscribeFromPush(authToken);
    if (result.success) {
      setIsSubscribed(false);
      setMessage({ type: 'success', text: 'Push notifications disabled.' });
    }
    setSubscribing(false);
  };

  const handlePrefChange = async (key) => {
    const updated = { ...preferences, [key]: !preferences[key] };
    setPreferences(updated);
    setSavingPrefs(true);
    try {
      await apiPut('/notifications/preferences', updated);
    } catch {
      setPreferences(preferences);
      setMessage({ type: 'error', text: 'Failed to save preferences.' });
    }
    setSavingPrefs(false);
  };

  const prefItems = [
    { key: 'price_drop_alerts', label: 'Price Drop Alerts', desc: 'Get notified when watched products drop in price' },
    { key: 'price_increase_alerts', label: 'Price Increase Alerts', desc: 'Get notified when watched products increase in price' },
    { key: 'receipt_reminders', label: 'Receipt Upload Reminders', desc: 'Reminders to upload receipts and earn points' },
    { key: 'promotions', label: 'Promotions & Offers', desc: 'Special deals and promotional notifications' },
    { key: 'email_fallback', label: 'Email Notifications', desc: 'Receive alerts by email when push is unavailable' },
  ];

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
      <h2 className="text-xl font-semibold mb-4 text-gray-700">Notification Settings</h2>

      {message && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 border border-green-300 text-green-700' : 'bg-red-50 border border-red-300 text-red-700'}`}>
          {message.text}
        </div>
      )}

      {/* Push Notification Toggle */}
      <div className="mb-6 pb-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-800">Push Notifications</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {!pushSupported
                ? 'Not supported in this browser'
                : pushPermission === 'denied'
                ? 'Blocked in browser settings'
                : isSubscribed
                ? 'Enabled — you\'ll receive alerts on this device'
                : 'Enable to receive price alerts on this device'}
            </p>
          </div>
          {pushSupported && pushPermission !== 'denied' && (
            <button
              onClick={isSubscribed ? handleDisablePush : handleEnablePush}
              disabled={subscribing}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                isSubscribed ? 'bg-red-800' : 'bg-gray-200'
              } ${subscribing ? 'opacity-50' : ''}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isSubscribed ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          )}
        </div>
      </div>

      {/* Notification Preferences */}
      {prefsLoading ? (
        <div className="text-center py-4">
          <div className="animate-spin h-5 w-5 border-b-2 border-red-800 mx-auto"></div>
        </div>
      ) : preferences && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Alert Types</h3>
          {prefItems.map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-gray-800">{label}</p>
                <p className="text-xs text-gray-500">{desc}</p>
              </div>
              <button
                onClick={() => handlePrefChange(key)}
                disabled={savingPrefs}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  preferences[key] ? 'bg-red-800' : 'bg-gray-200'
                } ${savingPrefs ? 'opacity-50' : ''}`}
              >
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${preferences[key] ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
